import type { CSSProperties, ReactElement } from "react";

import { ICONS, type IconName, type IconNode } from "./icons/paths";

/**
 * Иконка.
 *
 * Набор собран из двух источников (см. `scripts/build-icons.mjs`): контурные
 * иконки из пакета дизайна в сетке 24×24 и заливочные Phosphor в сетке
 * 256×256 — для экранов, которых в пакете не было. Форма записи у обоих
 * одинаковая, и разницу отрабатывает этот компонент, а не место вызова.
 *
 * Цвет наследуется через `currentColor`: иконка красится классом-токеном на
 * родителе, литеральных цветов в компоненте нет.
 */
interface IconProps {
  name: IconName;
  /** Размер в пикселях — берётся из макета, поэтому число, а не класс. */
  size?: number;
  /**
   * Толщина обводки в единицах сетки иконки (не в пикселях экрана).
   * Иконки нарисованы под 2 при размере 24; на крупных размерах — колокол в
   * шапке 40px, щит в предупреждении — контур становится волосяным, и его
   * приходится добирать.
   *
   * Намеренно НЕ `vectorEffect="non-scaling-stroke"`: он приколотил бы обводку
   * к постоянной толщине в пикселях экрана независимо от `size`, и крупная
   * иконка выглядела бы нарисованной другим инструментом.
   */
  weight?: number;
  className?: string;
  style?: CSSProperties;
}

function shape(node: IconNode, key: number): ReactElement {
  // Поузловые fill/stroke сохраняются как есть: в `ellipsis` точки нарисованы
  // заливкой посреди контурной иконки, и без переопределения они бы исчезли.
  const own = { fill: node.fill, stroke: node.stroke };

  switch (node.shape) {
    case "circle":
      return <circle key={key} cx={node.cx} cy={node.cy} r={node.r} {...own} />;
    case "ellipse":
      return <ellipse key={key} cx={node.cx} cy={node.cy} rx={node.rx} ry={node.ry} {...own} />;
    case "rect":
      return (
        <rect
          key={key}
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
          rx={node.rx}
          {...own}
        />
      );
    default:
      return <path key={key} d={node.d} {...own} />;
  }
}

export function Icon({ name, size = 18, weight = 2, className, style }: IconProps) {
  const icon = ICONS[name];

  // Почти все иконки квадратные, но не все: корона нарисована в сетке 32×24 —
  // она шире, чем выше. Ставить ей width=height=size значило бы сплющить её по
  // горизонтали. `size` трактуется как ВЫСОТА, ширина считается от пропорций
  // сетки: у квадратных ничего не меняется, у короны сохраняется рисунок.
  const width = Math.round((size * icon.width) / icon.height);

  const stroke = icon.stroke
    ? {
        fill: "none" as const,
        stroke: "currentColor",
        strokeWidth: weight,
        strokeLinecap: "round" as const,
        strokeLinejoin: "round" as const,
      }
    : { fill: "currentColor" };

  return (
    <svg
      viewBox={icon.viewBox}
      width={width}
      height={size}
      {...stroke}
      // Иконка всегда сопровождает текст или несёт aria-label на родителе,
      // самостоятельного смысла для скринридера не имеет.
      aria-hidden="true"
      focusable="false"
      className={className}
      style={style}
    >
      {icon.nodes.map(shape)}
    </svg>
  );
}
